'use client';

import styled from '@emotion/styled';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { GlobalNavigationMenu } from '@diritto/common/navigation/constant';
import { Color } from '@diritto/common/style/color';
import { HomeSectionViewModeSwitch } from 'app-components/home/HomeSectionViewModeSwitch';
import {
  DesktopContentWidth,
  DesktopNavigationWidth,
  GNBHeight,
  isPC,
  MobileGNBMenuHeight,
  PageContentBottomMargin,
} from 'constants/layout';
import { useAuthState } from 'contexts/AuthProvider';
import { Divider } from 'ui-components/common/Divider';
import { Footer } from 'ui-components/common/Footer';
import { PageTitle } from 'ui-components/common/PageTitle';
import { SafeAreaBottomStyles } from 'ui-components/layout/styles';
import { DesktopGlobalMenu } from 'ui-components/navigations/DesktopGlobalMenu';
import { GlobalNavigationBar } from 'ui-components/navigations/GlobalNavigationBar';
import { MobileGlobalMenu } from 'ui-components/navigations/MobileGlobalMenu';
import { useClient } from 'ui-hooks/useClient';
import { getMatchedMenu } from 'utils/navigation';
import { TopUp } from '@/app-components/promotions/TopUp';

interface HomeLayoutProps {
  children: React.ReactNode;
  title?: string;
  selectedMenu?: GlobalNavigationMenu;
}

export const HomeLayout = ({ children, title, selectedMenu }: HomeLayoutProps) => {
  const pathname = usePathname();
  const { isApp } = useClient();
  const currentMenu = useMemo(() => getMatchedMenu(pathname ?? ''), [pathname]);
  const footer = currentMenu && [GlobalNavigationMenu.Home].includes(currentMenu);
  const { partner } = useAuthState();

  return isApp ? (
    <StyledNavWrapper>
      <StyledMain>
        {children}
        {footer && (
          <>
            <Footer />
            <StyledViewModeSwitchWrapper>
              <HomeSectionViewModeSwitch />
            </StyledViewModeSwitchWrapper>
          </>
        )}
      </StyledMain>
    </StyledNavWrapper>
  ) : (
    <>
      <nav className="block relative bg-white w-full">
        <GlobalNavigationBar selectedMenu={selectedMenu ?? currentMenu} showPartnersLogo={!!partner?.verifiedAt} />
        <StyledDivider />
      </nav>
      <StyledNavWrapper>
        <DesktopGlobalMenu selectedMenu={selectedMenu ?? currentMenu} />
        <StyledMain>
          {title && <PageTitle title={title} />}
          {children}
          {footer && (
            <>
              <Footer />
              <StyledViewModeSwitchWrapper>
                <HomeSectionViewModeSwitch />
              </StyledViewModeSwitchWrapper>
            </>
          )}
        </StyledMain>
      </StyledNavWrapper>
      <TopUp className="pc:mb-0 mb-[56px]" />
      <MobileGlobalMenu selectedMenu={selectedMenu ?? currentMenu} />
    </>
  );
};

const StyledDivider = styled(Divider)`
  display: none;
  @media (${isPC}) {
    display: block;
  }
`;

const StyledNavWrapper = styled.div`
  width: 100%;
  margin: 0 auto;

  ${SafeAreaBottomStyles(MobileGNBMenuHeight)}

  @media (${isPC}) {
    display: flex;
    width: ${DesktopNavigationWidth}px;
    min-height: calc(100vh - ${GNBHeight}px);
    padding-bottom: 0;
  }
`;

const StyledMain = styled.main`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;

  @media (${isPC}) {
    width: ${DesktopContentWidth}px;
    border-left: 1px solid ${Color.Gray300};
    border-right: 1px solid ${Color.Gray300};
  }

  ${SafeAreaBottomStyles(PageContentBottomMargin)};
`;

const StyledViewModeSwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;
